/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router';
import { NavLink } from 'react-router-dom';
import { Selectors as StartUpSelectors, Status } from 'store/ducks/startUp';
import { PrivateRoutePath, PublicRoutePath } from 'routes/paths';
// Components
import logo from 'assets/images/vf-logo-icon-red.svg';
import { DomainUtils, Ducks, Role, User } from '@wit/vtv-x-foundation-lib';
import HeaderNavigation from './HeaderNavigation';
import HeaderMainMenu from './HeaderMainMenu';
import * as SearchDuck from '../../../store/ducks/search';
import Constants from '../../../configs/Constants';

export enum EScrollDirection {
  ScrollUp = 'scroll-up',
  ScrollDown = 'scroll-down',
  NoScroll = 'no-scroll',
}

interface IHeaderProps {
  isSearchDropdownVisible: boolean;
  onSearchClickHandler: (state: boolean) => void;
  onMenuActiveHandler: () => void;
}

const Header = React.memo(({ isSearchDropdownVisible, onSearchClickHandler, onMenuActiveHandler }: IHeaderProps) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const routerHistory = useHistory();
  const startUpStatus = useSelector(StartUpSelectors.status);
  const isStartUpSuccess = useSelector(StartUpSelectors.isStartUpSuccess);
  const selectedUserId = useSelector(Ducks.Credentials.Selectors.getUserId);
  const users: User[] = useSelector(Ducks.Household.Selectors.data.getUsers);
  const maxUsers = useSelector(Ducks.Household.Selectors.data.usersLimit);
  const isConverged = useSelector(Ducks.Household.Selectors.data.getIsConverged);

  const [profiles, setProfiles] = useState<User[]>([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const [scrollDir, setScrollDir] = useState<EScrollDirection>(EScrollDirection.NoScroll);
  useEffect(() => {
    let lastScrollY = window.pageYOffset;

    const updateScrollDir = () => {
      const scrollY = window.pageYOffset;
      if (scrollY < Constants.HEADER_TOP_OFFSET_TRIGGER_TO_FADE) {
        setScrollDir(EScrollDirection.NoScroll);
      } else {
        setScrollDir(scrollY > lastScrollY ? EScrollDirection.ScrollDown : EScrollDirection.ScrollUp);
        // Hide Settings dropdown on scrool down
        setDropdownVisible(false);
      }
      lastScrollY = scrollY > Constants.HEADER_INIT_TOP_OFFSET ? scrollY : Constants.HEADER_INIT_TOP_OFFSET;
    };

    const onScroll = () => {
      window.requestAnimationFrame(updateScrollDir);
    };

    window.addEventListener('scroll', onScroll);

    // Cleanup function
    return () => window.removeEventListener('scroll', onScroll);
  }, [scrollDir]);

  // set profiles
  useEffect(() => {
    const selectableUsers = DomainUtils.getSelectableUsers(isConverged, users);
    // Sort Users By Firstname and only if they have a role "normal"
    const sortedUsers = selectableUsers.sort((a: User, b: User) => {
      if (a.firstName && a.role === Role.NORMAL && b.firstName && b.role === Role.NORMAL) {
        return a.firstName.localeCompare(b.firstName);
      }
      return 0;
    });

    setProfiles(sortedUsers);
  }, [isConverged, users]);

  const handleMenu = (): void => {
    if (isStartUpSuccess) {
      setDropdownVisible((prevState) => !prevState);
      onSearchClickHandler(false);
    }
  };
  const handleSelectedProfile = (id: string): void => {
    setDropdownVisible((state) => !state);
    onSearchClickHandler(false);
    dispatch(Ducks.Credentials.Creators.switchUser(id, true));
    routerHistory.replace(PrivateRoutePath.WATCHTV);
  };

  const handleSignOut = (): void => {
    onSearchClickHandler(false);
    setDropdownVisible(false);
    dispatch(Ducks.Credentials.Creators.logout());
  };

  useEffect(() => {
    // Reset Search results on Close
    if (!isSearchDropdownVisible) {
      dispatch(SearchDuck.ActionCreators.searchReset());
    }
    // Scrool window to top only on Settings Subpages
    const dropdownPages =
      !isSearchDropdownVisible &&
      (window.location.href.includes(PrivateRoutePath.SETTINGS) ||
        window.location.href.includes(PublicRoutePath.HELP) ||
        window.location.href.includes(PublicRoutePath.PRIVACY));
    if (dropdownPages) {
      window.scrollTo(0, 0);
    }
  }, [isSearchDropdownVisible, location.pathname, dispatch]);

  const getRouteToNavigate = () => {
    switch (startUpStatus) {
      case Status.SUCCESS:
        return PrivateRoutePath.WATCHTV;
      case Status.WAITING_UI_USER_SWITCHING:
        return PrivateRoutePath.PROFILES_LIST;
      default:
        return '#';
    }
  };

  return (
    <div
      className={`component header-component ${scrollDir} ${isSearchDropdownVisible ? 'searchDropdownVisible' : ''}`}
    >
      <div className="header-inner">
        <div className="vodafone-logo">
          <NavLink id="Header_button___logo" to={getRouteToNavigate()}>
            <div onClick={() => onSearchClickHandler(false)}>
              <img id="Header_ButtonImage___logoFull" className="vf-logo-full" src={logo} alt="vodafone logo" />
            </div>
          </NavLink>
        </div>
        <HeaderNavigation
          active={isStartUpSuccess}
          searchDropdownVisible={isSearchDropdownVisible}
          dropdownVisible={dropdownVisible}
          onMenuActiveHandler={onMenuActiveHandler}
          setDropdownVisible={setDropdownVisible}
        />
        <div className="header-search-profile">
          {isStartUpSuccess && (
            <>
              <a
                id="Header_button___search"
                className={`header-search-icon ${isSearchDropdownVisible ? 'active' : ''}`}
                onClick={() => {
                  if (isStartUpSuccess) {
                    onSearchClickHandler(!isSearchDropdownVisible);
                    setDropdownVisible(false);
                  }
                }}
              />
              <a
                id="Header_button___profile"
                className={`header-profile-icon ${dropdownVisible ? 'active' : ''}`}
                onClick={() => handleMenu()}
              />
              {dropdownVisible && (
                <HeaderMainMenu
                  selectedProfileId={selectedUserId}
                  maxUsers={maxUsers}
                  profiles={profiles}
                  handleSelectedProfile={handleSelectedProfile}
                  handleSignOut={handleSignOut}
                  setDropdownVisible={setDropdownVisible}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default Header;
